import { useEffect } from "react";
import { Star } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { SEO } from "@/components/seo";

const testimonials = [
  {
    id: 1,
    name: "Marcus T.",
    tradingStyle: "Day Trader",
    rating: 5,
    text: "The Footprint Chart template completely changed how I read order flow. I can now see absorption and imbalances at a glance without spending hours configuring studies. My win rate improved noticeably within the first month of using it.",
  },
  {
    id: 2,
    name: "Sarah K.",
    tradingStyle: "Scalper",
    rating: 5,
    text: "As a scalper, speed is everything. The DOM template gives me a clean, fast layout with exactly the information I need. No clutter, no lag. The pre-configured hotkeys and color coding saved me days of setup time.",
  },
  {
    id: 3,
    name: "James L.",
    tradingStyle: "Swing Trader",
    rating: 4,
    text: "I bought the Volume Profile template to better identify key support and resistance zones on higher timeframes. The value area and POC levels are clearly displayed, and the template works seamlessly across multiple instruments.",
  },
  {
    id: 4,
    name: "David R.",
    tradingStyle: "Futures Trader",
    rating: 5,
    text: "The Market Profile TPO template is the best I've found for Sierra Chart. The session coloring, single prints highlighting, and initial balance markers are all perfectly configured. Worth every penny for serious futures traders.",
  },
  {
    id: 5,
    name: "Emily W.",
    tradingStyle: "Day Trader",
    rating: 5,
    text: "I was struggling to set up a proper heatmap in Sierra Chart on my own. This template nailed it — the color gradients, the tick sensitivity, everything is dialed in. Customer support was also incredibly responsive when I had a question.",
  },
  {
    id: 6,
    name: "Carlos M.",
    tradingStyle: "Scalper",
    rating: 4,
    text: "The Orderflow Suite indicator pack is a must-have for anyone trading ES or NQ. The absorption and delta divergence studies give real-time signals I can actually trust. Setup was straightforward with the included instructions.",
  },
  {
    id: 7,
    name: "Rebecca H.",
    tradingStyle: "Swing Trader",
    rating: 5,
    text: "I have been trading for over a decade and these are the most professionally built Sierra Chart templates I have come across. Clean layouts, logical color schemes, and they work right out of the box. Highly recommend to any serious trader.",
  },
  {
    id: 8,
    name: "Alex P.",
    tradingStyle: "Futures Trader",
    rating: 5,
    text: "Bought the full bundle and it was the best investment I have made for my trading setup this year. Every template is polished and well thought out. The volume sequence indicator alone has paid for itself many times over.",
  },
  {
    id: 9,
    name: "Nina F.",
    tradingStyle: "Day Trader",
    rating: 4,
    text: "Great templates with solid documentation. I appreciate that each product comes with clear installation steps and recommended settings. The footprint and heatmap templates are my daily go-to charts now.",
  },
  {
    id: 10,
    name: "Tom B.",
    tradingStyle: "Scalper",
    rating: 5,
    text: "Switching from another platform to Sierra Chart felt overwhelming until I found these templates. They gave me a professional setup from day one. The DOM and footprint layouts are exactly what I needed to hit the ground running.",
  },
];

function StarRating({ rating }: { rating: number }) {
  return (
    <div className="flex items-center gap-0.5" data-testid={`stars-rating-${rating}`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <Star
          key={i}
          className={`h-4 w-4 ${i < rating ? "fill-yellow-400 text-yellow-400" : "text-muted-foreground/30"}`}
        />
      ))}
    </div>
  );
}

export default function TestimonialsPage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
      <SEO
        title="Customer Testimonials"
        description="Read what professional traders say about Sierra Chart Pro templates and indicators. Real reviews from day traders, scalpers, swing traders, and futures traders."
        keywords="Sierra Chart reviews, trading template testimonials, Sierra Chart indicator reviews, trader feedback"
        canonical="/testimonials"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Product",
          name: "Sierra Chart Pro Templates & Indicators",
          aggregateRating: {
            "@type": "AggregateRating",
            ratingValue: "4.7",
            reviewCount: "10",
          },
        }}
      />

      <h1 className="text-2xl sm:text-3xl font-bold mb-2" data-testid="text-testimonials-title">
        Customer Testimonials
      </h1>
      <p className="text-muted-foreground mb-8" data-testid="text-testimonials-subtitle">
        Hear from traders who use our templates and indicators every day.
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {testimonials.map((t) => (
          <Card key={t.id} className="p-5" data-testid={`card-testimonial-${t.id}`}>
            <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-semibold text-sm" data-testid={`text-name-${t.id}`}>{t.name}</span>
                <Badge variant="secondary" data-testid={`badge-style-${t.id}`}>{t.tradingStyle}</Badge>
              </div>
              <StarRating rating={t.rating} />
            </div>
            <p className="text-sm text-muted-foreground leading-relaxed" data-testid={`text-review-${t.id}`}>
              {t.text}
            </p>
          </Card>
        ))}
      </div>
    </div>
  );
}
